import React, { useState, useEffect } from "react";
import axios from "axios";
import SummaryCard from "../components/SummaryCard";
import QuotationTable from "../components/QuotationTable";
import { apiURL } from "../config/config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import _ from "lodash";
import quotationTypes from "../data/quotationTypes.json";
import DashboardFilterBar from "../components/DashboardFilterBar";
import LoadingOverlay from "../components/LoadingOverlay";


const Dashboard = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const company = user?.company?.toUpperCase() || "";

  const [quotations, setQuotations] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");


  const availableTypes =
    company && quotationTypes.companies[company]
      ? Object.entries(quotationTypes.companies[company]).map(([label, value]) => ({
          label,
          value,
        }))
      : [];

  useEffect(() => {
    const fetchQuotations = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${apiURL}quotations`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = response.data;
        const pending = data.filter((q) => q.approvalStatus === "Pending").length;

        setQuotations(data);
        setFilteredQuotations(data);
        setPendingCount(pending);
      } catch (error) {
        console.error("Error fetching quotations:", error);
        toast.error("Failed to fetch quotations ❌");
      } finally {
        setLoading(false);
      }
    };

    fetchQuotations();
  }, []);

  useEffect(() => {
    let result = quotations;

    if (startDate || endDate) {
      result = result.filter((qt) => {
        const docDate = new Date(qt.documentDate);
        return (!startDate || docDate >= startDate) && (!endDate || docDate <= endDate);
      });
    }

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(
        (qt) =>
          qt.runNumber?.toLowerCase().includes(lowerSearch) ||
          qt.title?.toLowerCase().includes(lowerSearch) ||
          qt.clientId?.customerName?.toLowerCase().includes(lowerSearch) ||
          qt.period?.toLowerCase().includes(lowerSearch)
      );
    }    

    if (selectedType) {
      result = result.filter((qt) => qt.type === selectedType);
    }

    if (selectedStatus) {
      result = result.filter((qt) => qt.approvalStatus === selectedStatus);
    }

    setFilteredQuotations(result);
  }, [startDate, endDate, searchTerm, selectedType, selectedStatus, quotations]);

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredQuotations].sort((a, b) => {
      if (a[key] < b[key]) return direction === "ascending" ? -1 : 1;
      if (a[key] > b[key]) return direction === "ascending" ? 1 : -1;
      return 0;
    });

    setFilteredQuotations(sorted);
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "ascending" ? " ↑" : " ↓";
    }
    return "";
  };

  const exportToExcel = async () => {
    if (filteredQuotations.length === 0) {
      toast.error("ไม่มีข้อมูลสำหรับ Export ❌");
      return;
    }

    try {
      const excelData = filteredQuotations.map((qt) => {
        const formattedDocumentDate = qt.documentDate
          ? new Date(qt.documentDate).toLocaleDateString("th-TH", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "-";

        const formatNumber = (num) => (_.isNumber(num) ? _.round(num, 2) : "-");

        let rowData = {
          สถานะการอนุมัติ: qt.approvalStatus || "-",
          เลขที่เอกสาร: `${qt.companyPrefix || "OPTX"}(${qt.type || "M"})-${new Date(qt.documentDate).getFullYear()}-${qt.runNumber}`,
          วันที่เอกสาร: formattedDocumentDate,
          พนักงานขาย: qt.salePerson || "-",
          ชื่อลูกค้า: qt.clientId?.customerName || "-",
          ที่อยู่: qt.clientId?.address || "-",
          เลขประจำตัวผู้เสียภาษี: qt.clientId?.taxIdentificationNumber || "-",
          "รหัส สาขา": qt.clientId?.branchCode || "00000",
          หมายเลขโทรศัพท์ติดต่อ: qt.clientId?.contactPhoneNumber || "-",
          ชื่อโครงการ: qt.projectName || "-",
          วันที่จัดงาน: qt.period || "-",
          รวม: formatNumber(qt.total),
          ส่วนลด: formatNumber(qt.discount),
          จำนวนเงินก่อนภาษี: formatNumber(qt.amountBeforeTax),
          ภาษีมูลค่าเพิ่ม: formatNumber(qt.vat),
          จำนวนเงินสุทธิ: formatNumber(qt.netAmount),
          รายละเอียดการชำระเงิน: `ระยะเวลา Credit Term ${qt.CreditTerm || 0} วัน`,
        };

        let itemsData = {};
        qt.items.forEach((item, index) => {
          itemsData[`รายละเอียด (${index + 1})`] = item.description || "-";
          itemsData[`หน่วยนับ (${index + 1})`] = _.toString(item.unit || "-");
          itemsData[`ราคาต่อหน่วย (${index + 1})`] = formatNumber(item.unitPrice);
          itemsData[`จำนวนเงิน (${index + 1})`] = formatNumber(item.amount);
        });

        const lastIndex = qt.items.length;
        itemsData[`รายละเอียด (${lastIndex + 1})`] = `Fee (${qt.fee}%)` || "-";
        itemsData[`หน่วยนับ (${lastIndex + 1})`] = "1";
        itemsData[`ราคาต่อหน่วย (${lastIndex + 1})`] = 1;
        itemsData[`จำนวนเงิน (${lastIndex + 1})`] = formatNumber(qt.calFee);

        return { ...rowData, ...itemsData };
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Quotations");

      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let row = range.s.r; row <= range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          const cell = worksheet[cellAddress];
          if (!cell) continue;
          if (cell.v && !isNaN(cell.v) && typeof cell.v === "number" && cell.v !== "-") {
            cell.z = "#,##0.00";
            cell.s = { alignment: { horizontal: "right" } };
          }
          if (typeof cell.v === "string" && cell.v !== "-") {
            cell.t = "s";
          }
        }
      }

      XLSX.writeFile(workbook, `Quotations_${new Date().toISOString()}.xlsx`);
      toast.success("Export สำเร็จ ✅");
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toast.error("Export ล้มเหลว ❌");
    }
  };

  return (
    <div className="p-6 relative">
      <LoadingOverlay loading={loading} />

      {user && (
        <h1 className="text-2xl font-bold text-gray-800 mb-4">
          Welcome back, {user.firstName}!
        </h1>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <SummaryCard title="Pending Approvals" value={pendingCount} bgColor="bg-blue-100" textColor="text-blue-800" />
        <SummaryCard title="Total Quotations" value={quotations.length} bgColor="bg-green-100" textColor="text-green-800" />
      </div>

      <div className="bg-white shadow rounded p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <h2 className="text-lg font-semibold">All Quotations</h2>
          <DashboardFilterBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            availableTypes={availableTypes}
            onClear={() => {
              setStartDate(null);
              setEndDate(null);
              setSearchTerm("");
              setSelectedType("");
              setSelectedStatus(""); 
            }}
            onExport={exportToExcel}
          />
        </div>

        {filteredQuotations.length > 0 ? (
          <QuotationTable
            quotations={filteredQuotations}
            sortConfig={sortConfig}
            handleSort={handleSort}
            getSortIndicator={getSortIndicator}
          />
        ) : (
          <p className="text-gray-500">No quotations found.</p>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
